import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AppState, Linking } from 'react-native';
import Ionicons from '@expo/vector-icons/Ionicons';
import {
  Alert,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from '../components/rn';
import ActivityIndicator from '../components/LoadingIndicator';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import PageHeader from '../components/PageHeader';
import { screenHeaderContentInset } from '../components/screenHeaderLayout';
import { api, InstagramConnectionStatus } from '../services/api';
import { fontSize, spacing, ThemeColors, radius } from '../theme';
import { useTheme } from '../theme/ThemeContext';
import CompactActionButton from '../components/CompactActionButton';
import { useTranslation } from '../i18n/I18nContext';
import { uppercaseForDisplayLanguage } from '../utils/locale';
import { InfoButton, InfoText, useInfo } from '../components/InfoHint';
import InstagramArchiveImport from '../components/InstagramArchiveImport';
import { loadOwnMediaKeys } from '../instagramArchive/ownMedia';

type Props = { token: string; onBack: () => void };

export default function InstagramConnectionsScreen({ token, onBack }: Props) {
  const [importOpen, setImportOpen] = useState(false);
  const insets = useSafeAreaInsets();
  const colors = useTheme();
  // 7.14 / D-654 — sayfanın açıklaması ve izin notu ⓘ arkasında.
  const pageInfo = useInfo();
  const { t, lang } = useTranslation();
  const styles = useMemo(() => makeStyles(colors), [colors]);
  const [status, setStatus] = useState<InstagramConnectionStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [failed, setFailed] = useState(false);

  const load = useCallback(async (showLoading = true) => {
    if (showLoading) setLoading(true);
    setFailed(false);
    try {
      setStatus(await api.getInstagramConnection(token));
    } catch {
      setFailed(true);
    } finally {
      if (showLoading) setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const subscription = AppState.addEventListener('change', (state) => {
      if (state === 'active') load(false);
    });
    return () => subscription.remove();
  }, [load]);

  async function connect() {
    if (!status?.available || connecting) return;
    setConnecting(true);
    try {
      const result = await api.startInstagramAuthorization(token, lang);
      await Linking.openURL(result.authorizationUrl);
    } catch {
      Alert.alert(t('common.error'), t('instagram.connectionFailed'));
    } finally {
      setConnecting(false);
    }
  }

  const accountType = status?.connection?.accountType === 'BUSINESS'
    ? t('instagram.business')
    : t('instagram.creator');

  return (
    <View style={styles.container}>
      <PageHeader
        title={t('instagram.title')}
        onBack={onBack}
        floating
        action={<InfoButton open={pageInfo.open} onPress={pageInfo.toggle} subject={t('instagram.title')} testID="instagram-connections-info" />}
      />
      {loading ? (
        <View style={styles.center}><ActivityIndicator color={colors.black} /></View>
      ) : (
        <ScrollView contentContainerStyle={[styles.content, { paddingTop: screenHeaderContentInset(insets.top) }]}>
          <InfoText open={pageInfo.open} text={t('instagram.connectInfo')} style={styles.description} testID="instagram-connections-description" />
          <InfoText open={pageInfo.open} text={t('instagram.privacyInfo')} style={styles.description} testID="instagram-connections-privacy" />
          <Text style={styles.sectionLabel}>
            {uppercaseForDisplayLanguage(t('instagram.professionalAccount'), lang)}
          </Text>
          <View style={styles.section}>
            <View style={styles.row}>
              <View style={styles.icon}><Ionicons name="logo-instagram" size={22} color={colors.black} /></View>
              <View style={styles.copy}>
                <Text style={styles.rowTitle}>Instagram</Text>
                <Text style={styles.hint}>
                  {failed
                    ? t('discover.failed')
                    : status?.connected && status.connection
                    ? t('instagram.connectedAs', { username: status.connection.username, accountType })
                    : status?.available
                      ? t('instagram.notConnected')
                      : t('instagram.unavailable')}
                </Text>
              </View>
              {!!status?.available && (
                <CompactActionButton
                  label={status.connected ? t('instagram.reconnect') : t('instagram.connect')}
                  onPress={connect}
                  loading={connecting}
                  style={styles.rowAction}
                  testID="instagram-connect"
                />
              )}
            </View>
          </View>
          {failed && (
            <CompactActionButton
              variant="plain"
              label={t('common.retry')}
              onPress={() => load()}
              style={styles.retry}
              testID="instagram-retry"
            />
          )}
          {/* 9.18.10.4 · D-244/D-830 — every account, personal ones included,
              can bring its photos and videos from its own Instagram download. */}
          <Text style={styles.sectionLabel}>
            {uppercaseForDisplayLanguage(t('instagramImport.archiveSection'), lang)}
          </Text>
          <View style={styles.section}>
            <View style={styles.row}>
              <View style={styles.icon}><Ionicons name="download-outline" size={22} color={colors.black} /></View>
              <View style={styles.copy}>
                <Text style={styles.rowTitle}>{t('instagramImport.addEntry')}</Text>
                <Text style={styles.hint}>{t('instagramImport.archiveHint')}</Text>
              </View>
              <CompactActionButton
                label={t('common.continue')}
                onPress={() => setImportOpen(true)}
                style={styles.rowAction}
                testID="instagram-archive-import-open"
              />
            </View>
          </View>
        </ScrollView>
      )}
      {importOpen && <InstagramArchiveImport
        token={token}
        visible
        loadExisting={() => loadOwnMediaKeys(token)}
        onClose={() => setImportOpen(false)}
      />}
    </View>
  );
}

const makeStyles = (colors: ThemeColors) => StyleSheet.create({
  container: { flex: 1, backgroundColor: colors.white },
  description: { fontSize: fontSize.sm, color: colors.gray600, marginBottom: spacing.sm },
  center: { flex: 1, alignItems: 'center', justifyContent: 'center' },
  content: { padding: spacing.lg, paddingBottom: spacing.xl * 2 },
  sectionLabel: { fontSize: fontSize.sm, color: colors.gray400, marginTop: spacing.md, marginBottom: spacing.sm },
  section: { backgroundColor: colors.gray100, borderRadius: radius.card, paddingHorizontal: spacing.md },
  row: { minHeight: 72, flexDirection: 'row', alignItems: 'center', gap: spacing.sm },
  icon: { width: 36, height: 36, borderRadius: radius.control, backgroundColor: colors.white, alignItems: 'center', justifyContent: 'center' },
  copy: { flex: 1, minWidth: 0 },
  rowTitle: { fontSize: fontSize.md, color: colors.black, fontWeight: '500' },
  hint: { fontSize: fontSize.sm, color: colors.gray400, marginTop: 2 },
  // D-410 — geometri ve 44 pt dokunma hedefi `CompactActionButton`'da.
  rowAction: { minWidth: 82, maxWidth: 132, flexShrink: 1 },
  notice: { fontSize: fontSize.sm, lineHeight: 20, color: colors.gray400, marginTop: spacing.lg },
  retry: { alignSelf: 'flex-start', marginTop: spacing.md, paddingHorizontal: 0 },
  retryText: { color: colors.black, fontWeight: '600' },
});
